import { useSelector } from 'react-redux'
import { useCallback, useMemo, useState } from 'react'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import {
  getActiveSessions,
  isLoggedIn,
  terminateSessions,
} from '$logic/auth/service'
import { useAsyncFunction } from '$func/async-function.react-hook'
import { showSaveChangesLoadingModal } from '$comp/modals/loading-modal'

export function useIsLoggedIn() {
  return useSelector(() => isLoggedIn())
}

const NO_TERMINATED_SELECTORS: string[] = []

export function useActiveSessions() {
  const [update, setUpdate] = useState(0)
  /**
   * Sessions terminated from this view, hidden locally instead of refetching
   * the whole list after every click. The list request is throttled server-side
   * (10/min), so clearing a long device list one item at a time used to burn
   * the quota and lock the user out of channels entirely.
   */
  const [terminatedSelectors, setTerminatedSelectors] = useState(
    NO_TERMINATED_SELECTORS,
  )

  const res = useAsyncFunction(getActiveSessions, [update])

  const value = useMemo(() => {
    const fetched = res.value
    // `undefined` while loading, `null` on failure, `'TOO_MANY_REQUESTS'` when
    // the request was throttled — none of them carry a list to filter.
    if (!fetched || typeof fetched === 'string') return fetched
    if (!terminatedSelectors.length) return fetched

    const terminated = new Set(terminatedSelectors)
    return {
      ...fetched,
      others: fetched.others.filter(
        session => !terminated.has(session.selector),
      ),
    }
  }, [res.value, terminatedSelectors])

  const refetch = useCallback(() => {
    setTerminatedSelectors(NO_TERMINATED_SELECTORS)
    setUpdate(current => current + 1)
  }, [])

  const terminateOtherSessions = async () => {
    if (!value || typeof value === 'string') return
    showSaveChangesLoadingModal()
    await terminateSessions(value.others.map(session => session.selector))
    await closeCurrentModal()
    refetch()
  }

  const terminateSession = async (selector: string) => {
    await terminateSessions([selector])
    setTerminatedSelectors(current =>
      current.includes(selector) ? current : [...current, selector],
    )
  }

  return { ...res, value, terminateSession, terminateOtherSessions }
}
